import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { Globe, ArrowLeft, CheckCircle } from 'lucide-react';

const ResetPassword = () => {
  const { requestPasswordReset, confirmPasswordReset } = useAuth();
  const [step, setStep] = useState(1); // 1: request, 2: confirm, 3: success
  const [email, setEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await requestPasswordReset(email);
      // For demo purposes, we show the token (in production, this would be sent via email)
      if (response.token) {
        setResetToken(response.token);
      }
      setStep(2);
      toast.success('Reset instructions sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reset email');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmReset = async (e) => {
    e.preventDefault();
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      await confirmPasswordReset(resetToken, newPassword);
      setStep(3);
      toast.success('Password updated successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side - Image */}
      <div 
        className="hidden lg:flex lg:w-1/2 relative bg-cover bg-center"
        style={{
          backgroundImage: `url('https://images.pexels.com/photos/6699772/pexels-photo-6699772.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940')`
        }}
      >
        <div className="absolute inset-0 bg-[#0F172A]/80"></div>
        <div className="relative z-10 flex flex-col justify-center px-12 xl:px-20">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Globe className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-heading font-semibold text-white">Our Orbis</h1>
          </div>
          <h2 className="text-4xl xl:text-5xl font-heading font-semibold text-white leading-tight mb-6">
            Secure Your<br />
            <span className="text-[#D4AF37]">Account Access</span>
          </h2>
          <p className="text-slate-300 text-lg max-w-md leading-relaxed">
            Reset your password to regain access to your professional network management platform.
          </p>
        </div>
      </div>

      {/* Right side - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-10 justify-center">
            <div className="w-10 h-10 rounded-full bg-[#D4AF37] flex items-center justify-center">
              <Globe className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-2xl font-heading font-semibold text-[#0F172A]">Our Orbis</h1>
          </div>

          <Link 
            to="/login" 
            className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 mb-8"
            data-testid="back-to-login"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          {step === 1 && (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-heading font-semibold text-[#0F172A] mb-2">
                  Reset password
                </h2>
                <p className="text-slate-500">
                  Enter your email and we'll send you reset instructions
                </p>
              </div>

              <form onSubmit={handleRequestReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700 font-medium">
                    Email Address
                  </Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="reset-email-input"
                    className="h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="reset-submit-button"
                  className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium text-base"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Sending...
                    </div>
                  ) : (
                    'Send Reset Instructions'
                  )}
                </Button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <div className="mb-8">
                <h2 className="text-3xl font-heading font-semibold text-[#0F172A] mb-2">
                  Enter new password
                </h2>
                <p className="text-slate-500">
                  Choose a secure password for your account
                </p>
              </div>

              <form onSubmit={handleConfirmReset} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="token" className="text-slate-700 font-medium">
                    Reset Token
                  </Label>
                  <Input
                    id="token"
                    type="text"
                    placeholder="Paste your reset token"
                    value={resetToken}
                    onChange={(e) => setResetToken(e.target.value)}
                    required
                    data-testid="reset-token-input"
                    className="h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37] font-mono text-sm"
                  />
                  <p className="text-xs text-slate-400">
                    Check your email for the reset token
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="newPassword" className="text-slate-700 font-medium">
                    New Password
                  </Label>
                  <Input
                    id="newPassword"
                    type="password"
                    placeholder="At least 6 characters"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    data-testid="new-password-input"
                    className="h-12 border-slate-200 focus:border-[#D4AF37] focus:ring-[#D4AF37]"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="confirm-reset-button"
                  className="w-full h-12 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium text-base"
                >
                  {loading ? (
                    <div className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></div>
                      Updating...
                    </div>
                  ) : (
                    'Update Password'
                  )}
                </Button>
              </form>
            </>
          )}

          {step === 3 && (
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-3xl font-heading font-semibold text-[#0F172A] mb-2">
                Password updated!
              </h2>
              <p className="text-slate-500 mb-8">
                Your password has been successfully reset
              </p>
              <Link to="/login">
                <Button
                  data-testid="back-to-login-button"
                  className="h-12 px-8 bg-[#0F172A] hover:bg-[#1E293B] text-white font-medium text-base"
                >
                  Sign in with new password
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
